import React from "react";
import { useI18n } from "../i18n";
import NewsletterForm from "../components/NewsletterForm";

const projectImages = [
  "/images/project-torres.jpg",
  "/images/project-pozo.jpg",
  "/images/project-reformas.jpg",
  "/images/project-complejo.jpg",
];

export default function Home() {
  const { t, dict } = useI18n();

  return (
    <main data-testid="page-home">
      {/* HERO */}
      <section className="border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p
              className="text-xs uppercase tracking-widest font-bold text-[#1E3A8A]"
              data-testid="hero-eyebrow"
            >
              {t("hero.eyebrow")}
            </p>
            <h1
              className="mt-5 font-serif font-medium text-[#101828] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
              data-testid="hero-title"
            >
              {t("hero.title")}
            </h1>
            <p className="mt-6 text-lg text-[#475467] leading-relaxed max-w-xl">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8">
              <a
                href="#newsletter"
                data-testid="hero-cta-btn"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById("newsletter");
                  if (el) el.scrollIntoView({ block: "start" });
                }}
                className="inline-block bg-[#1E3A8A] text-white px-6 h-12 leading-[3rem] text-sm font-semibold tracking-wide hover:bg-[#1E40AF] no-underline"
              >
                {t("hero.cta")}
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative aspect-[4/5] w-full border border-gray-200 overflow-hidden bg-gray-100">
              <img
                src="/images/hero-skyline.jpg"
                alt={t("hero.imageAlt")}
                className="h-full w-full object-cover"
                loading="eager"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="border-b border-gray-200 bg-[#F5F5F4]">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-20 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="text-xs uppercase tracking-widest font-bold text-[#475467]">
              {t("intro.label")}
            </p>
            <h2 className="mt-4 font-serif font-medium text-3xl md:text-4xl text-[#101828] tracking-tight leading-[1.15]">
              {t("intro.title")}
            </h2>
          </div>
          <div className="md:col-span-8 md:col-start-6 space-y-6">
            {dict.intro.paragraphs.map((p, i) => (
              <p
                key={i}
                className="text-base md:text-lg text-[#475467] leading-relaxed"
              >
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-20 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <p className="text-xs uppercase tracking-widest font-bold text-[#475467]">
                {t("projects.label")}
              </p>
              <h2 className="mt-4 font-serif font-medium text-3xl md:text-4xl text-[#101828] tracking-tight leading-[1.15]">
                {t("projects.title")}
              </h2>
            </div>
            <div className="md:col-span-7">
              <p className="text-base md:text-lg text-[#475467] leading-relaxed">
                {t("projects.intro")}
              </p>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
            {dict.projects.items.map((item, idx) => {
              const spans = [
                "md:col-span-7",
                "md:col-span-5",
                "md:col-span-5",
                "md:col-span-7",
              ];
              return (
                <article
                  key={idx}
                  className={`${spans[idx]} border border-gray-200 bg-white flex flex-col`}
                  data-testid={`project-card-${idx}`}
                >
                  <div className="aspect-[16/9] w-full bg-gray-100 overflow-hidden">
                    <img
                      src={projectImages[idx]}
                      alt={item.imageAlt}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6 md:p-8">
                    <h3 className="font-serif text-2xl text-[#101828] tracking-tight">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-sm md:text-base text-[#475467] leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-b border-gray-200 bg-[#0C111D] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <p className="text-xs uppercase tracking-widest font-bold text-white/60">
                {t("how.label")}
              </p>
              <h2 className="mt-4 font-serif font-medium text-3xl md:text-4xl tracking-tight leading-[1.15]">
                {t("how.title")}
              </h2>
            </div>
            <div className="md:col-span-7">
              <ol className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                {dict.how.steps.map((step, i) => (
                  <li
                    key={i}
                    className="bg-[#0C111D] p-6 md:p-8"
                    data-testid={`how-step-${i}`}
                  >
                    <span className="font-serif text-3xl text-white/60">
                      {step.number}
                    </span>
                    <h3 className="mt-3 font-serif text-xl tracking-tight">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-sm text-white/70 leading-relaxed">
                      {step.description}
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-10">
            <h3 className="md:col-span-5 font-serif text-2xl md:text-3xl tracking-tight leading-tight">
              {t("how.afterTitle")}
            </h3>
            <div className="md:col-span-7 space-y-5">
              {dict.how.afterParagraphs.map((p, i) => (
                <p key={i} className="text-base text-white/70 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-20 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="text-xs uppercase tracking-widest font-bold text-[#475467]">
              {t("principles.label")}
            </p>
            <h2 className="mt-4 font-serif font-medium text-3xl md:text-4xl text-[#101828] tracking-tight leading-[1.15]">
              {t("principles.title")}
            </h2>
          </div>
          <div className="md:col-span-8 md:col-start-6 space-y-6">
            {dict.principles.paragraphs.map((p, i) => (
              <p
                key={i}
                className="text-base md:text-lg text-[#475467] leading-relaxed"
              >
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="bg-[#F5F5F4]">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <p className="text-xs uppercase tracking-widest font-bold text-[#1E3A8A]">
                {t("newsletter.label")}
              </p>
              <h2 className="mt-4 font-serif font-medium text-3xl md:text-4xl text-[#101828] tracking-tight leading-[1.15]">
                {t("newsletter.title")}
              </h2>
              <p className="mt-5 text-base md:text-lg text-[#475467] leading-relaxed">
                {t("newsletter.subtitle")}
              </p>
            </div>
            <div className="lg:col-span-7 bg-white border border-gray-200 p-6 md:p-10">
              <NewsletterForm />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
